import React, {forwardRef, useEffect, useMemo, useReducer, useRef, useImperativeHandle, useState} from 'react';
import {FormGroup} from '@blueprintjs/core';
import {useSelector} from "react-redux";

import {useCurrentUser} from '../../../hooks';
import {BaseBlock, ContentActions} from '../types';
import {BLOCK_TYPE, defaultContentActions} from '../constants';
import {
  Attachments,
  ContentCard,
  ContentToolbar,
  DownloadAttachments,
  MergePdf,
  MessageResult,
  NoIntegration,
  NoIntegrationCallout,
  SendWithAllAttachments,
} from '../components';
import {
  useCreateBlock,
  useExecuteRequest,
  useGetResult,
  useInitBlock,
  useUpdateBlockContentJson
} from '../hooks';
import {createDefaultContent} from './utils';
import {
  contentReducer,
  setContentFormKeyValue,
  setContentFormKeysValues,
  unsetPreview,
  setPreview,
} from '../store';
import {initialEmailContentState} from './store';
import {emailContentOptions} from './constants';
import {EmailContentOptions} from './types';
import Recipients from './Recipients';
import Subject from './Subject';
import EmailMessage from './EmailMessage'
import {smtpSelector} from "../../../store/reducers/integrationReducer";
import {integrationToSelectOption, toggleTinyReadOnly} from "../utils";
import SelectFromEmail from "./SelectFromEmail";
import {MergeCsv} from "../components/MergeCsv";

import './Email.scss';

interface EmailProps {
  content: BaseBlock;
  options?: EmailContentOptions;
  actions?: ContentActions;
}

interface MergeCsvOption {
  sheet_name?: string;
  position?: number;
  include?: boolean;
  uid?: string;
}

interface MergeCsvConfig {
  enabled: boolean;
  filename: string;
  options: MergeCsvOption[];
}

interface MergePdfOption {
  position?: number;
  include?: boolean;
  uid?: string;
}

interface MergePdfConfig {
  enabled: boolean;
  filename: string;
  options: MergePdfOption[];
}

export const Email = forwardRef(({
  content,
  options = emailContentOptions,
  actions = defaultContentActions,
}: EmailProps, ref: any) => {
  const emailMessageRef = useRef(null);
  const [contentState, contentDispatch] = useReducer(contentReducer, initialEmailContentState);
  const [selectedIntegrationId, setSelectedIntegrationId] = useState(contentState.form.integration_id);
  const [selectedSmtpFromEmail, setSelectedSmtpFromEmail] = useState(content.smtp_from);
  const currentUser = useCurrentUser();
  const defaultToRecipient = currentUser?.email || '';

  useEffect(() => {
    setSelectedIntegrationId(contentState.form.integration_id);
  }, [contentState.form.integration_id]);

  options = useMemo((): EmailContentOptions => ({...emailContentOptions, ...options}), [options?.readOnly]);
  actions = useMemo((): ContentActions => ({...defaultContentActions, ...actions}), []);
  const uid = content.uid;

  const {result} = useGetResult(uid, content.result_id, options.loadResult);
  const {executing, executeRequest} = useExecuteRequest(uid);
  const {updating, updateBlockContentJson} = useUpdateBlockContentJson(uid, options.readOnly, options.autosave);

  let smtpIntegrations = useSelector(smtpSelector) || [];
  const noSmtpIntegrations = smtpIntegrations.length === 0;

  const create = useCreateBlock(content, createDefaultContent(defaultToRecipient));
  useInitBlock(uid, content, contentDispatch);

  useImperativeHandle(ref, () => ({
    getInit() {
      return contentState.init;
    },
    getEditor() {
      return emailMessageRef.current;
    },
    reInitEditor() {
    },
    setAttachments(attachments: string[]) {
      /*
       * TODO: remove this and find a better logic to update attachments
       *
       * Attachments can come from an outer source (notebook, report) as they're calculated based on the available
       *   content in the outer source.
       */

      onAttachmentChange(attachments);
    },
    getMergePdfConfig(): MergePdfConfig {
      return contentState.form.merge_pdf_config || {};
    },
    setMergePdfConfig(value: MergePdfConfig) {
      onMergePdfConfigChange(value);
    },
    getMergeCsvConfig(): MergeCsvConfig {
      return contentState.form.merge_csv_config || {};
    },
    setMergeCsvConfig(value: MergeCsvConfig) {
      onMergeCsvConfigChange(value);
    },
  }));

  const onExecute = () => {
    const block: any = {
      content_json: {
        subject: contentState.form.subject,
        value: contentState.form.value,
        recipients: contentState.form.recipients || defaultToRecipient,
        cc: contentState.form.cc,
        bcc: contentState.form.bcc,
        smtp_from: contentState.form.smtp_from,
        smtp_from_fullname: contentState.form.smtp_from_fullname,
        attachments: contentState.form.attachments,
        integration_id: contentState.form.integration_id,
        merge_pdf_config: contentState.form.merge_pdf_config,
        merge_csv_config: contentState.form.merge_csv_config,
        send_with_all_attachments: contentState.form.send_with_all_attachments,
      }
    };
    executeRequest(block);
  };

  const onAttachmentChange = (attachments: string[]) => {
    contentDispatch(setContentFormKeyValue('attachments', attachments));
    updateBlockContentJson('attachments', attachments);
  };

  const onMergePdfConfigChange = (value: MergePdfConfig) => {
    contentDispatch(setContentFormKeyValue('merge_pdf_config', value));
    updateBlockContentJson('merge_pdf_config', value);
  };

  const onMergeCsvConfigChange = (value: MergeCsvConfig) => {
    contentDispatch(setContentFormKeyValue('merge_csv_config', value));
    updateBlockContentJson('merge_csv_config', value);
  };

  const onSendWithAllAttachmentsChange = (value: boolean) => {
    contentDispatch(setContentFormKeyValue('send_with_all_attachments', value));
    updateBlockContentJson('send_with_all_attachments', value);
  };

  const onRecipientChange = (key: string) => (value: any) => {
    const _value = value.join(',');

    contentDispatch(setContentFormKeyValue(key, _value));
    updateBlockContentJson(key, _value, false);
  };

  const onSubjectChange = (value: string) => {
    contentDispatch(setContentFormKeyValue('subject', value));
    updateBlockContentJson('subject', value);
  };

  const onMessageChange = (value: any) => {
    contentDispatch(setContentFormKeyValue('value', value));
    updateBlockContentJson('value', value);
  }

  const onSmtpIntegrationIdChange = (integrationUid: string) => {
    setSelectedIntegrationId(integrationUid);

    const selectedIntegration = smtpIntegrations.find(i => i.uid === integrationUid);
    const emailAddress = selectedIntegration?.config_json?.email_addresses[0].value;
    const emailName = selectedIntegration?.config_json?.email_addresses[0].name;
    const emailFullName = `${emailName} <${emailAddress}>`;
    setSelectedSmtpFromEmail(emailAddress);

    const smtp_content = {
      'integration_id': integrationUid,
      'smtp_from': emailAddress,
      'smtp_from_fullname': emailFullName
    };

    const keys = Object.keys(smtp_content);
    const values = Object.values(smtp_content);
    onKeysValuesChange(keys, values);
  };

  const onKeysValuesChange = (keys: string[], values: any[]) => {
    /*
     * TODO: change this from [...keys], [...values] to {key: value}
     */
    updateBlockContentJson(keys, values);
    contentDispatch(setContentFormKeysValues(keys, values));
  };

  const onEmailFromChange = (value: any) => {
    const _value = value.value;
    const _label = value.label;

    const smtp_content = {
      'smtp_from': _value,
      'smtp_from_fullname': _label,
    };

    const keys = Object.keys(smtp_content);
    const values = Object.values(smtp_content);
    onKeysValuesChange(keys, values);
  };

  const onPreview = (data: any) => {
    const editor = emailMessageRef.current;

    if (!data) {
      contentDispatch(unsetPreview());

      toggleTinyReadOnly(editor, null, false);
    } else {
      try {
        const jsonData = JSON.parse(data);
        const {recipients, cc, bcc, subject, value} = jsonData;

        toggleTinyReadOnly(editor, value, true);
        contentDispatch(setPreview({
          bcc,
          cc,
          recipients,
          subject,
        }));
      } catch (e) {
        console.log("Error parsing JSON", e);
      }
    }
  };

  return (
    <ContentCard
      key={content.id}
      type={content.type}
      contentId={contentState.init ? uid : null}
      disabled={noSmtpIntegrations || options.readOnly}
      noContentElement={noSmtpIntegrations ? <NoIntegration contentId={uid as string} type={BLOCK_TYPE.SMTP}/> : null}
      allowCollapse={options.allowCollapse}
      showHeader={options.showHeader}
    >
      <div className="content__email">
        <ContentToolbar
          contentId={uid as string}
          executing={executing}
          onExecute={options.showRun ? onExecute : null}
          selected={contentState.form.integration_id}
          onSelect={onSmtpIntegrationIdChange}
          options={smtpIntegrations.map(integrationToSelectOption)}
          updating={updating}
          selectLabel="Select SMTP Integration"
          onDelete={actions.onDelete}
          showDelete={options.showDelete}
          showPreview={options.showPreview}
          showSave={options.showSave}
          disabled={options.readOnly}
          onPreview={onPreview}
        />
        <div className="content-card__content__form">
          {
            !contentState.form.integration_id ? <NoIntegrationCallout/> : (
              <>
                <SelectFromEmail
                  integrations={smtpIntegrations}
                  integration_id={selectedIntegrationId}
                  smtpFromEmail={selectedSmtpFromEmail}
                  onChange={onEmailFromChange}
                  readOnly={contentState.isPreview || options.readOnly}
                />
                <Recipients
                  to={create ? (contentState.form.recipients || defaultToRecipient) : contentState.form.recipients}
                  cc={contentState.form.cc}
                  bcc={contentState.form.bcc}
                  onChange={onRecipientChange}
                  disabled={contentState.isPreview || options.readOnly}
                  preview={contentState.isPreview}
                />
                <div className="content__email__body">
                  {
                    options.showAttachments && (
                      <>
                        {
                          !options.showDownloadAttachments && contentState.init && (
                            <Attachments
                              value={contentState.form.attachments}
                              onChange={onAttachmentChange}
                              disabled={contentState.isPreview || options.readOnly}
                            />
                          )
                        }
                        {
                          options.showDownloadAttachments && !!content.result_id && (
                            <DownloadAttachments
                              uid={uid as string}
                              resultId={content.result_id}
                              value={contentState.form.attachments}
                            />
                          )
                        }
                        {
                          options.showMergePdf && (
                            <MergePdf
                              config={contentState.form.merge_pdf_config || {}}
                              onConfigChange={onMergePdfConfigChange}
                              attachments={contentState.form.attachments}
                              disabled={contentState.isPreview || options.readOnly}
                            />
                          )
                        }
                        {
                          options.showMergeCsv && (
                            <MergeCsv
                              config={contentState.form.merge_csv_config || {}}
                              onConfigChange={onMergeCsvConfigChange}
                              attachments={contentState.form.attachments}
                              disabled={contentState.isPreview || options.readOnly}
                            />
                          )
                        }
                      </>
                    )
                  }
                  <SendWithAllAttachments
                    value={contentState.form.send_with_all_attachments}
                    onChange={onSendWithAllAttachmentsChange}
                    disabled={
                      contentState.isPreview ||
                      options.readOnly ||
                      (contentState.form.attachments?.length || 0) === 0
                    }
                  />
                  <Subject
                    value={contentState.form.subject || ''}
                    onChange={onSubjectChange}
                    disabled={contentState.isPreview || options.readOnly || false}
                  />
                  <FormGroup label="Message" className="email-message">
                    <EmailMessage
                      ref={emailMessageRef}
                      defaultValue={content.value || ''}
                      onChange={onMessageChange}
                      disabled={options.readOnly}
                    />
                  </FormGroup>
                </div>
                <MessageResult
                  result={result}
                />
              </>
            )
          }
        </div>
      </div>
    </ContentCard>
  )
});

export default Email;
